import { AfterViewChecked, ChangeDetectorRef, Component } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { DecimalPipe } from 'app/shared/decimal.pipe';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';

declare const Mangler: any;

@Component({
	selector: 'app-modal-bulk-edit-subtask',
	templateUrl: './modal-bulk-edit-subtask.component.html',
	styleUrls: ['./modal-bulk-edit-subtask.component.scss'],
})
export class ModalBulkEditSubtaskComponent extends UIModalBaseComponent implements AfterViewChecked {

	title = 'Bulk Edit Subtasks';
	step: 'select' | 'edit' = 'select';
	disabled = false;

	parent;
	dependencyOptions = [];

	groups = [];
	rows = [];

	uniqueGroups = [];
	commonDiffLines = [];
	lastChangedItem;
	lastChangedItemId = null;
	changedProperties: any = [];
	propertiesMap = {
		name: 'Name',
		description: 'Description',
		duration: 'Duration',
		duration_type: 'Duration Type',
		dependencies: 'Dependencies',
		priority: 'Priority'
	};

	get selectedRows() {
		return this.rows.filter(row => row.selected);
	}

	get noScroll() {
		if (this.step === 'select') return false;
		return this.selectedRows.length < 3;
	}

	constructor(
		public app: AppService,
		private api: ApiService,
		private cdr: ChangeDetectorRef,
	) {
		super();
	}

	ngAfterViewChecked() {
		this.cdr.detectChanges();
	}

	open(parent: any) {
		this.parent = parent;
		this.step = 'select';
		this.disabled = false;
		this.groups = [];
		this.rows = [];
		this.resetPropagation();

		(parent.sub_tasks || []).forEach(group => {
			const groupRows = [];
			(group.sub_tasks || []).forEach(sub => {
				if (sub.stage === 'completed') return;
				const row = this.buildRow(sub);
				groupRows.push(row);
				this.rows.push(row);
			});
			if (groupRows.length) {
				const all = group.sub_tasks || [];
				this.groups.push({
					group_key: group.group_key,
					rows: groupRows,
					collapsed: true,
					total: all.length,
					completed: all.filter(s => s.stage === 'completed').length,
					dependencies: group.dependencies || []
				});
			}
		});

		if (!this.rows.length) {
			this.app.notifications.showInfo('There are no editable subtasks to bulk edit.');
			return;
		}

		// Load the org dependency list, then open
		this.disabled = true;
		this.api.task.listDependencies(deps => {
			this.dependencyOptions = deps || [];
			this.mergeAssignedDependencies();
			this.disabled = false;
			this.modal.open();
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	buildRow(sub) {
		const dependencies = sub.dependencies || [];
		const dependencyIds = dependencies.map(d => d.id);
		const lockedDependencyIds = dependencies.filter(d => d.done).map(d => d.id);

		// Duration is stored in hours; show it in minutes when the subtask uses a "min" unit
		let duration = sub.duration || 0;
		if (sub.duration_type === 'min') duration *= 60;

		return {
			id: sub.id,
			project_id: sub.project_id,
			parent_id: sub.parent_id,
			work_order_id: sub.work_order_id,
			structure_id: sub.structure_id,
			category_id: sub.category_id,
			stage: sub.stage,
			type: sub.type,
			billable: sub.billable,
			image_url: sub.image_url,
			area: sub.area,
			category: sub.category,
			similar_key: sub.name || '',

			name: sub.name || '',
			description: sub.description || '',
			duration: DecimalPipe.transform(duration, 0, 4),
			duration_type: sub.duration_type || 'hour',
			priority: String(sub.priority || 0),

			dependencyIds,
			lockedDependencyIds,
			originalDependencyIds: [...dependencyIds],

			selected: false
		};
	}

	// Ensure any dependency already assigned to a subtask stays selectable even if it has since been archived
	mergeAssignedDependencies() {
		const optionIds = this.dependencyOptions.map(d => d.id);
		this.rows.forEach(row => {
			(this.parentDependencies(row.id) || []).forEach(dep => {
				if (optionIds.indexOf(dep.id) === -1) {
					this.dependencyOptions.push({ id: dep.id, name: dep.name, description: dep.description, type: dep.type });
					optionIds.push(dep.id);
				}
			});
		});
	}

	parentDependencies(subId) {
		for (const group of (this.parent.sub_tasks || [])) {
			const sub = Mangler.first(group.sub_tasks, { id: subId });
			if (sub) return sub.dependencies || [];
		}
		return [];
	}

	toggleRow(row) {
		row.selected = !row.selected;
	}

	removeRow(row) {
		row.selected = false;
	}

	toggleGroupCollapsed(group) {
		group.collapsed = !group.collapsed;
	}

	isGroupAllSelected(group) {
		return group.rows.length > 0 && group.rows.every(row => row.selected);
	}

	toggleGroupSelection(group) {
		const select = !this.isGroupAllSelected(group);
		group.rows.forEach(row => row.selected = select);
	}

	getCompletedDependencies(dependencies: any[]): number {
		return (dependencies || []).filter(dep => dep.done === 1).length;
	}

	confirmSelection() {
		if (!this.selectedRows.length) return;
		this.resetPropagation();
		this.buildUniqueGroups();
		this.step = 'edit';
	}

	backToSelection() {
		this.resetPropagation();
		this.step = 'select';
	}

	// --- Edit step: field changes + "apply to similar / all" propagation ---

	rowDependencyChange(ids, row) {
		row.dependencyIds = ids;
		this.checkForChangedProperties('dependencies', row);
	}

	durationChanged(row) {
		row.duration = DecimalPipe.transform(row.duration, 0, 4);
		this.checkForChangedProperties('duration', row);
	}

	// True when the row's dependency selection differs from what it started with
	hasDependencyOverride(row) {
		const original = row.originalDependencyIds || [];
		const current = row.dependencyIds || [];
		if (original.length !== current.length) return true;
		return original.some(id => current.indexOf(id) === -1);
	}

	buildUniqueGroups() {
		const seen = new Map();
		this.selectedRows.forEach(row => {
			if (!seen.has(row.similar_key)) {
				seen.set(row.similar_key, {
					similar_key: row.similar_key,
					name: row.name,
					description: row.description,
					duration: row.duration,
					duration_type: row.duration_type,
					priority: row.priority,
					dependencies: [...row.dependencyIds]
				});
			}
		});
		this.uniqueGroups = Array.from(seen.values());
	}

	compareItems(item1, item2) {
		if (Array.isArray(item1) && Array.isArray(item2)) {
			return [...item1].sort().join(',') === [...item2].sort().join(',');
		}
		return item1 === item2;
	}

	commonPropertyChanged(row) {
		const unique = Mangler.first(this.uniqueGroups, { similar_key: row.similar_key });
		if (!unique) return null;
		for (const property of this.changedProperties) {
			const value = property === 'dependencies' ? row.dependencyIds : row[property];
			if (!this.compareItems(value, unique[property])) return row;
		}
		return null;
	}

	checkForChangedProperties(property, row) {
		if (this.lastChangedItem && this.lastChangedItem.id !== row.id) this.changedProperties = [];
		this.lastChangedItem = { ...row };
		this.lastChangedItemId = row.id;
		if (!this.changedProperties.includes(property)) this.changedProperties.push(property);
		this.commonDiffLines = [];
		const changedRow = this.commonPropertyChanged(row);
		if (changedRow) {
			this.commonDiffLines = this.selectedRows.filter(r => r.similar_key === changedRow.similar_key);
		}
	}

	applyChangedProperties(row) {
		if (this.changedProperties.includes('name')) row.name = this.lastChangedItem['name'];
		if (this.changedProperties.includes('description')) row.description = this.lastChangedItem['description'];
		if (this.changedProperties.includes('duration')) row.duration = DecimalPipe.transform(this.lastChangedItem['duration'], 0, 4);
		if (this.changedProperties.includes('duration_type')) row.duration_type = this.lastChangedItem['duration_type'];
		if (this.changedProperties.includes('priority')) row.priority = this.lastChangedItem['priority'];
		if (this.changedProperties.includes('dependencies')) {
			// Never drop a target row's completed (locked) dependency when applying in bulk
			row.dependencyIds = Array.from(new Set([...(row.lockedDependencyIds || []), ...this.lastChangedItem['dependencyIds']]));
		}
	}

	bulkUpdateCommonProperties() {
		this.selectedRows.forEach(row => {
			if (row.similar_key === this.lastChangedItem['similar_key']) this.applyChangedProperties(row);
		});
		this.resetPropagation();
	}

	bulkUpdateAllCommonProperties() {
		this.selectedRows.forEach(row => this.applyChangedProperties(row));
		this.resetPropagation();
	}

	cancelUpdate() {
		this.resetPropagation();
	}

	resetPropagation() {
		this.uniqueGroups = [];
		this.commonDiffLines = [];
		this.lastChangedItem = null;
		this.lastChangedItemId = null;
		this.changedProperties = [];
	}

	isSelectedInput(row, prop) {
		return this.commonDiffLines.length - 1 > 0
			&& this.lastChangedItem
			&& row.similar_key === this.lastChangedItem['similar_key']
			&& this.changedProperties.includes(prop);
	}

	save() {
		const rows = this.selectedRows;
		if (!rows.length) return;

		for (const row of rows) {
			if (!('' + row.name).trim()) {
				this.app.notifications.showDanger('Subtask name cannot be empty.');
				return;
			}
		}

		const payloads = rows.map(row => {
			let duration = DecimalPipe.parse(row.duration);
			if (row.duration_type === 'min') duration /= 60;

			return {
				id: row.id,
				project_id: row.project_id,
				parent_id: row.parent_id,
				work_order_id: row.work_order_id,
				structure_id: row.structure_id,
				category_id: row.category_id,
				stage: row.stage,
				type: row.type,
				name: row.name,
				description: row.description,
				duration,
				duration_type: row.duration_type,
				billable: row.billable,
				priority: row.priority,
				// Always re-submit locked (completed) dependencies so their captured outcomes are never deleted
				dependencies: Array.from(new Set([...(row.lockedDependencyIds || []), ...(row.dependencyIds || [])]))
			};
		});

		this.disabled = true;
		this.saveNext(payloads, 0);
	}

	saveNext(payloads, index) {
		if (index >= payloads.length) {
			this.disabled = false;
			this.app.notifications.showSuccess(payloads.length === 1 ? 'Subtask updated.' : 'Subtasks updated.');
			this.app.navigation.refreshBillableTaskCount();
			this.modal.close('saved');
			return;
		}

		this.api.task.save(payloads[index], () => {
			this.saveNext(payloads, index + 1);
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

}
